import { dropAllContexts } from '@/utils/navigationContext';
import { ROUTE_CHILDREN_MAP } from '@/components/Navigation/navigationChildRoutes';
import { screenPath, screenPrefix } from '@/router/screenRoutes';

export const isPath = (value) => typeof value === 'string' && value.startsWith('/') && value !== '/';

export function isAllowedInReadOnly(path, paths) {
  if (typeof path !== 'string' || !Array.isArray(paths)) return false;

  return paths.some((allowed) => isPath(allowed) && (path === allowed || path.startsWith(`${allowed}/`)));
}

export function readOnlyMenu(routes = [], paths = []) {
  return routes.filter((route) => isAllowedInReadOnly(route?.href, paths));
}

export function sessionLanding({ consult, menuDefault }) {
  if (isPath(consult?.landing)) return consult.landing;

  return isPath(menuDefault) ? menuDefault : null;
}

export function consultScope(consult) {
  return Array.isArray(consult?.paths) && isPath(consult?.landing) ? consult : null;
}

export function readOnlyExit({ consult, path }) {
  const scope = consultScope(consult);

  if (!scope || isAllowedInReadOnly(path, scope.paths)) return null;

  return scope.landing;
}

export const BLOCK_REASON_CNPJ_INACTIVE = 'cnpj_inactive';

// Screens that exist for the person, reached from the user menu rather than the sidebar.
const BLOCKED_AUXILIARY_PATHS = ['edit-user', 'terms', 'guidelines'].map(screenPath);

export const BLOCKED_MESSAGE = 'Seu acesso está temporariamente indisponível. '
  + 'Entre em contato com seu comercial.';

export function isBlockedSession(userInfo) {
  return userInfo?.promoter_block_reason === BLOCK_REASON_CNPJ_INACTIVE;
}

function startsAtPath(path, base) {
  return path === base || path.startsWith(`${base}/`);
}

export function readStoredMenuHrefs() {
  try {
    const routers = JSON.parse(localStorage.getItem('menu'))?.routers;

    if (!Array.isArray(routers)) return [];

    return routers
      .map((route) => route?.href)
      .filter((href) => typeof href === 'string' && href.startsWith('/') && href !== '/');
  } catch {
    return [];
  }
}

// Detail screens that hang off a menu item without sharing its prefix.
const BLOCKED_EXTRA_CHILDREN = {
  [screenPath('promoters')]: ['accordance', 'accordance-edit'].map(screenPath),
};

// `:param` matches one segment, `:param?` may be absent, and extra trailing segments are allowed.
function matchesRoute(path, pattern) {
  const pathSegments = path.split('/').filter(Boolean);

  return pattern.split('/').filter(Boolean).every((segment, index) => {
    if (!segment.startsWith(':')) return pathSegments[index] === segment;

    return segment.endsWith('?') || index < pathSegments.length;
  });
}

const BLOCKED_DENIED_PATHS = [screenPrefix('cgv-update-proposal')];

// The login menu is the allowlist for a blocked session; auxiliary screens have no menu item.
export function isAllowedWhenBlocked(path, menuHrefs = readStoredMenuHrefs()) {
  if (typeof path !== 'string') return false;
  if (BLOCKED_DENIED_PATHS.some((denied) => startsAtPath(path, denied))) return false;

  const children = menuHrefs.flatMap((href) => [
    ...(ROUTE_CHILDREN_MAP[href] || []),
    ...(BLOCKED_EXTRA_CHILDREN[href] || []),
  ]);

  return [...menuHrefs, ...BLOCKED_AUXILIARY_PATHS].some((base) => startsAtPath(path, base))
    || children.some((pattern) => matchesRoute(path, pattern));
}

function readStoredMenuDefault() {
  try {
    return JSON.parse(localStorage.getItem('menu'))?.default;
  } catch {
    return null;
  }
}

// The blocked menu may lack the proposals screen, so the fixed fallback can be off-limits.
export function blockedLandingPath() {
  const menuDefault = readStoredMenuDefault();
  if (isAllowedWhenBlocked(menuDefault)) return menuDefault;

  return readStoredMenuHrefs()[0] || BLOCKED_AUXILIARY_PATHS[0];
}

export function pathOf(url) {
  if (typeof url !== 'string') return '';

  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return '';
  }
}

export function consultExit({
  userInfo, path, active = userInfo?.promoter_active !== false, resolvePath = (target) => target,
}) {
  const landing = isBlockedSession(userInfo)
    ? !active && !isAllowedWhenBlocked(path) && blockedLandingPath()
    : readOnlyExit({ consult: userInfo?.consult, path });

  if (!landing || resolvePath(landing) === path) return null;

  return landing;
}

/**
 * A switch is a new session in everything but the login: filters, pagination and
 * navigation contexts all describe the promoter being left behind. Carrying them
 * over would show one promoter's filters against another's data.
 */
export function applyPromoterSession({ token, setToken }) {
  dropAllContexts();

  try {
    sessionStorage.clear();
  } catch {
    // storage unavailable
  }

  setToken(token);
}
